import { extractShopifyObjectId } from '@petplate/utils/shopify';
import type { AnalyticsPlugin } from 'analytics';

declare global {
  interface Window {
    TriplePixel?: (...args: unknown[]) => unknown;
    TriplePixelData?: Record<string, unknown>;
  }
}

interface TriplePixelPluginConfig {
  shop: string;
}

const CONFIG_SECURITY_ORIGINS = [
  'https://api.config-security.com/',
  'https://conf.config-security.com/'
];

export const triplePixelSnippet = (shop: string): string => {
  const tripleName = JSON.stringify(shop);
  const host = encodeURIComponent(shop);
  return `window.TriplePixelData={TripleName:${tripleName},ver:"2.18",plat:"SHOPIFY",isHeadless:true},function(W,H,A,L,E,_,B,N){function O(U,T,P,H,R){void 0===R&&(R=!1),H=new XMLHttpRequest,P?(H.open("POST",U,!0),H.setRequestHeader("Content-Type","text/plain")):H.open("GET",U,!0),H.send(JSON.stringify(P||{})),H.onreadystatechange=function(){4===H.readyState&&200===H.status?(R=H.responseText,U.includes("/first")?eval(R):P||(N[B]=R)):(299<H.status||H.status<200)&&T&&!R&&(R=!0,O(U,T-1,P))}}if(N=window,!N[H+"sn"]){N[H+"sn"]=1,L=function(){return Date.now().toString(36)+"_"+Math.random().toString(36)};try{A.setItem(H,1+(0|A.getItem(H)||0)),(E=JSON.parse(A.getItem(H+"U")||"[]")).push({u:location.href,r:document.referrer,t:Date.now(),id:L()}),A.setItem(H+"U",JSON.stringify(E))}catch(e){}var i,m,p;A.getItem('"!nC\`')||(_=A,A=N,A[H]||(E=A[H]=function(t,e,a){return void 0===a&&(a=[]),"State"==t?E.s:(W=L(),(E._q=E._q||[]).push([W,t,e].concat(a)),W)},E.s="Installed",E._q=[],E.ch=W,B="configSecurityConfModel",N[B]=1,O("https://conf.config-security.com/model",5),O("https://api.config-security.com/first?host=${host}&plat=SHOPIFY",5)))}}("","TriplePixel",localStorage);`;
};

export const triplePixelPlugin = (config: TriplePixelPluginConfig): AnalyticsPlugin => {
  let lastTrackedHref: string | undefined;

  return {
    name: 'triple-pixel',
    config,
    initialize: () => {
      if (typeof window === 'undefined') return;
      lastTrackedHref = window.location.href;
      if (window.TriplePixel) return;

      CONFIG_SECURITY_ORIGINS.forEach((href) => {
        const link = document.createElement('link');
        link.rel = 'preconnect dns-prefetch';
        link.href = href;
        link.crossOrigin = '';
        document.head.appendChild(link);
      });

      const script = document.createElement('script');
      script.text = triplePixelSnippet(config.shop);
      document.head.appendChild(script);
    },
    page: () => {
      if (typeof window === 'undefined' || window.location.href === lastTrackedHref) return;
      lastTrackedHref = window.location.href;
      window.TriplePixel?.('pageLoad');
    },
    loaded: () => typeof window !== 'undefined'
  };
};

export const setTriplePixelContact = ({ email }: { email?: string }) => {
  if (!email || typeof window === 'undefined') return;
  window.TriplePixel?.('Contact', { email });
};

type ShopifyProductIds = {
  productId?: string | null;
  variantId?: string | null;
};

const numericShopifyIds = ({ productId, variantId }: ShopifyProductIds) => {
  const product = extractShopifyObjectId(productId);
  const variant = extractShopifyObjectId(variantId);
  return product && variant ? { product, variant } : null;
};

export const setTriplePixelAddToCart = (items: (ShopifyProductIds & { quantity: number })[]) => {
  if (typeof window === 'undefined') return;
  items.forEach(({ quantity, ...shopifyIds }) => {
    const ids = numericShopifyIds(shopifyIds);
    if (!ids) return;
    window.TriplePixel?.('AddToCart', { item: ids.product, q: quantity, v: ids.variant });
  });
};

export const setTriplePixelProductView = ({
  name,
  price,
  ...shopifyIds
}: ShopifyProductIds & { name?: string; price?: number }) => {
  if (typeof window === 'undefined') return;
  const ids = numericShopifyIds(shopifyIds);
  if (!ids) return;
  window.TriplePixel?.('pageLoad', {
    product: {
      id: ids.product,
      name,
      price: Number.isFinite(price) ? String(price) : undefined,
      variant: ids.variant
    }
  });
};
